import AppLoader from "@/components/AppLoader";
import Header from "@/components/header/Header";
import NotificationBar from "@/components/NotificationBar";
import OnboardingDialog from "@/components/onboarding/OnboardingDialog";
import SlideOutModal from "@/components/SlideOutModal";
import Toaster from "@/components/ui/toaster";
import { useBackgroundPreloader } from "@/contexts/BackgroundPreloaderContext";
import { useModaleLocation } from "@/contexts/ModaleLocationContext";
import SmartLabel from "@/domain/enums/SmartLabel";
import useGetBanner from "@/hooks/useGetBanner";
import useGetUserProfile from "@/hooks/useGetUserProfile";
import { LAYOUT } from "@/lib/layoutConstants";
import { cn } from "@/lib/utils";
import Vistassistant from "@/presentation/dashboard/modalPages/mails/Vistassistant";
import routes, { modaleConfig, smartLabelRootUrl } from "@/router/routes";
import assistantStore from "@/stores/assistantStore";
import { setDeepFocusActive } from "@/stores/deepFocusStore";
import { setOpenedEvent } from "@/stores/eventStore";
import { useSelector } from "@tanstack/react-store";
import { useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

const focusSmartLabelPath = `/${smartLabelRootUrl}/${SmartLabel.Focus}`;
const modalDefaultPath = modaleConfig.defaultPathWhenModalOpened;

const DashboardLayout = () => {
    const { data: userProfile, isLoading: isUserProfileLoading } =
        useGetUserProfile();
    const { data: bannerData } = useGetBanner();
    const { isBackgroundLoaded, backgroundImageUrl } = useBackgroundPreloader();
    const location = useLocation();
    const navigate = useNavigate();
    const browserPath = window.location.pathname;
    const { modaleLocation } = useModaleLocation();

    const openModalAndNavigate = () => {
        setOpenedEvent(undefined);
        navigate(modaleLocation ?? modalDefaultPath, { replace: false });
    };

    useEffect(() => {
        const isOnDashboard = browserPath === modaleConfig.pathWhenModalClosed;
        const isOnFocusRoute =
            location.pathname.startsWith(focusSmartLabelPath);
        if (isOnDashboard || !isOnFocusRoute) {
            setDeepFocusActive(false);
        }
    }, [location.pathname, browserPath]);

    const isDashboardPage = useMemo(
        () => window.location.pathname === routes.dashboard,
        [location],
    );
    const [hideNotificationBar, setHideNotificationBar] = useState(false);
    const isAssistantOpen = useSelector(assistantStore, (s) => s.isOpen);

    const showNotificationBar: boolean = useMemo(
        () =>
            Boolean(bannerData) &&
            isDashboardPage &&
            !hideNotificationBar &&
            !isAssistantOpen,
        [bannerData, isDashboardPage, hideNotificationBar, isAssistantOpen],
    );

    if (!userProfile || isUserProfileLoading || !isBackgroundLoaded) {
        return <AppLoader />;
    }

    return (
        <>
            <div
                className="flex h-screen w-full flex-col overflow-hidden"
                style={{
                    backgroundImage: `url(${backgroundImageUrl})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                }}
            >
                {bannerData && (
                    <NotificationBar
                        bannerData={bannerData}
                        isVisible={showNotificationBar}
                        setHideNotificationBar={setHideNotificationBar}
                    />
                )}
                <div className="relative h-full">
                    <div
                        className={cn(
                            "absolute top-0 h-40 w-full bg-gradient-to-b from-black to-transparent transition-all duration-300",
                        )}
                    />
                    <div
                        className={cn(
                            "absolute bottom-0 left-0 right-0 top-0 h-full px-12 transition-all duration-300",
                        )}
                    >
                        <div className="relative flex h-full flex-col">
                            <Header />
                            <div
                                className={cn(
                                    "relative h-full w-full",
                                    LAYOUT.contentPaddingTop,
                                )}
                            >
                                <SlideOutModal
                                    assistantModalContent={<Vistassistant />}
                                    mainModalContent={<Outlet />}
                                    onRequestOpen={openModalAndNavigate}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Toaster />
            <OnboardingDialog />
        </>
    );
};

export default DashboardLayout;
